class GWPManager {
  constructor() {
    this.cart = null;
    this.retryLimit = 3;
    this.init();
  }

  init() {
    document.addEventListener('cart:update', () => this.handleCartUpdate());
    this.handleCartUpdate();
  }

  async handleCartUpdate() {
    try {
      this.cart = await this.fetchCart();
      await this.processGWP();
    } catch (e) {
      console.error('Error handling cart update:', e);
    }
  }

  async fetchCart(retries = 0) {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error('Cart fetch failed');
      return await res.json();
    } catch (e) {
      console.error('Error fetching cart:', e);
      if (retries < this.retryLimit) {
        await new Promise(resolve => setTimeout(resolve, 500));
        return this.fetchCart(retries + 1);
      }
      throw e;
    }
  }

  async addGift(productId, message, discountType, retries = 0) {
    const data = {
      id: productId,
      quantity: 1,
      properties: {
        _gwp: true,
        _gwp_message: message,
        _gwp_discount_type: discountType
      },
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: window.location.pathname
    };
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error('Add gift failed');
      return await res.json().then(data => {
        //document.dispatchEvent(new CustomEvent('refresh:cart:drawer'));
        return data;
      });
    } catch (e) {
      if (retries < this.retryLimit) {
        await new Promise(resolve => setTimeout(resolve, 500));
        return this.addGift(productId, message, discountType, retries + 1);
      }
    }
  }

  async removeGift(productId) {
    const item = this.cart.items.find(
      i => i.id === parseInt(productId) && i.properties && i.properties._gwp === true
    );
    if (item) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: item.key || item.id,
            quantity: 0,
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname
          })
        });
        if (res.ok) {
          return await res.json().then(data => {
            return data
          });
        }
      } catch (e) {
        console.error('Error removing gift:', e);
      }
    }
  }

  dispatchCartUpdate(data) {
    if (!data) return;
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: null,
          sourceId: 'gwp-manager',
          data: {
            sections: data.sections,
            itemCount: data.item_count,
            source: 'gwp-manager',
          },
        },
      })
    );
  }

  async processGWP() {
    // GWP - Cart Amount
    if (window.gwpCartMoreThan) {
      const giftId = window.gwpCartMoreThan.giftProduct;
      const excludedTotal = window.gwpCartMoreThan.excludedProduct
        ? this.cart.items
            .filter(i => i.product_id === window.gwpCartMoreThan.excludedProduct)
            .reduce((sum, i) => sum + i.line_price, 0)
        : 0;
      const qualifies = (this.cart.total_price - excludedTotal) / 100 >= window.gwpCartMoreThan.cartAmount;
      if (qualifies && !this.hasGift(giftId)) {
        if (window.gwpCartMoreThan.showModal) {
          if (!sessionStorage.getItem('gwp-modal-claimed')) {
            this.showGWPModal();
          }
        } else {
          await this.addGift(
            giftId,
            'Gift for cart amount',
            'cart_amount'
          ).then((data) => this.dispatchCartUpdate(data));
        }
      } else if (!qualifies) {
        sessionStorage.removeItem('gwp-modal-claimed');
        if (this.hasGift(giftId)) {
          await this.removeGift(giftId).then((data) => this.dispatchCartUpdate(data));
        }
      }
    }
    // GWP - If Item in Cart
    if (window.gwpIfItem) {
      const giftId = window.gwpIfItem.giftProduct;
      if (
        this.hasItem(window.gwpIfItem.triggerItem) &&
        !this.hasGift(giftId)
      ) {
        await this.addGift(
          giftId,
          'Gift for item in cart',
          'item_in_cart'
        ).then((data) => this.dispatchCartUpdate(data));
      } else if (
        !this.hasItem(window.gwpIfItem.triggerItem) &&
        this.hasGift(giftId)
      ) {
        await this.removeGift(giftId).then((data) => this.dispatchCartUpdate(data));
      }
    }
    // GWP - Buy 2 Get 1 Free
    if (window.gwpBuy2Get1Free) {
      const giftId = window.gwpBuy2Get1Free.giftProduct;
      const qty = this.getItemQty(window.gwpBuy2Get1Free.triggerItem);
      if (qty >= 2 && !this.hasGift(giftId)) {
        await this.addGift(
          giftId,
          'Buy 2 get 1 free',
          'buy_2_get_1'
        ).then((data) => this.dispatchCartUpdate(data));
      } else if (qty < 2 && this.hasGift(giftId)) {
        await this.removeGift(giftId).then((data) => this.dispatchCartUpdate(data));
      }
    }
  }

  showGWPModal() {
    const modal = document.getElementById('gwp-modal');
    if (!modal || modal.open) return;

    // Reset button state from any previous open
    const addBtnReset = document.getElementById('gwp-modal-add-btn');
    if (addBtnReset) addBtnReset.disabled = false;

    modal.showModal();

    const addBtn = document.getElementById('gwp-modal-add-btn');
    const closeBtn = modal.querySelector('.gwp-modal__close');

    const cleanup = () => {
      addBtn?.removeEventListener('click', handleAdd);
      closeBtn?.removeEventListener('click', handleClose);
      modal.removeEventListener('cancel', handleCancel);
      modal.removeEventListener('click', handleBackdrop);
    };

    const addAndClose = async () => {
      cleanup();
      if (addBtn) addBtn.disabled = true;
      const giftId = window.gwpCartMoreThan.giftProduct;
      const data = await this.addGift(giftId, 'Gift for cart amount', 'cart_amount');
      modal.close();
      if (data) {
        sessionStorage.setItem('gwp-modal-claimed', '1');
        this.dispatchCartUpdate(data);
      }
    };

    const handleAdd = () => addAndClose();

    const handleClose = () => addAndClose();

    const handleCancel = (e) => {
      e.preventDefault();
      addAndClose();
    };

    const handleBackdrop = (e) => {
      if (e.target === modal) addAndClose();
    };

    addBtn?.addEventListener('click', handleAdd);
    closeBtn?.addEventListener('click', handleClose);
    modal.addEventListener('cancel', handleCancel);
    modal.addEventListener('click', handleBackdrop);
  }

  hasGift(productId) {
    return this.cart.items.some(
      item => item.id === parseInt(productId) && item.properties && item.properties._gwp === true 
    );
  }

  hasItem(productId) {
    return this.cart.items.some(item => item.id === productId);
  }

  getItemQty(productId) {
    const item = this.cart.items.find(i => i.id === productId);
    return item ? item.quantity : 0;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new GWPManager();
});